import React, { useState, useEffect } from 'react';
import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Typography,
    TextField,
    InputAdornment,
    Chip,
    IconButton,
    Divider,
    Alert,
    Skeleton,
    Badge
} from '@mui/material';
import { Search, Star, Crown, Wand2, Sparkles, X } from 'lucide-react';

// Sample template data
const sampleTemplates = [
    {
        id: 1,
        name: "Modern Blue Gradient",
        theme: "modern",
        color: "#2563eb",
        layout: "horizontal",
        includePhoto: true,
        includeQR: true,
        premium: false,
        aiGenerated: false,
        industry: ["Technology", "Marketing", "Design"],
        popular: true,
        imageUrl: "/api/placeholder/200/100"
    },
    {
        id: 2,
        name: "Classic Corporate",
        theme: "classic",
        color: "#000000",
        layout: "horizontal",
        includePhoto: true,
        includeQR: false,
        premium: false,
        aiGenerated: false,
        industry: ["Finance", "Legal", "Consulting"],
        popular: true,
        imageUrl: "/api/placeholder/200/100"
    },
    {
        id: 3,
        name: "Minimal Tech",
        theme: "minimal",
        color: "#8B5CF6",
        layout: "vertical",
        includePhoto: true,
        includeQR: true,
        premium: false,
        aiGenerated: false,
        industry: ["Technology", "Startups"],
        popular: false,
        imageUrl: "/api/placeholder/200/100"
    },
    {
        id: 4,
        name: "Creative Agency",
        theme: "modern",
        color: "#EC4899",
        layout: "horizontal",
        includePhoto: true,
        includeQR: true,
        premium: true,
        aiGenerated: false,
        industry: ["Design", "Marketing", "Media"],
        popular: false,
        imageUrl: "/api/placeholder/200/100"
    },
    {
        id: 5,
        name: "Healthcare Professional",
        theme: "classic",
        color: "#10B981",
        layout: "vertical",
        includePhoto: true,
        includeQR: false,
        premium: true,
        aiGenerated: true,
        industry: ["Healthcare", "Medical"],
        popular: false,
        imageUrl: "/api/placeholder/200/100"
    },
    {
        id: 6,
        name: "Executive Suite",
        theme: "classic",
        color: "#4B5563",
        layout: "horizontal",
        includePhoto: true,
        includeQR: false,
        premium: true,
        aiGenerated: true,
        industry: ["Executive", "Finance", "Legal"],
        popular: true,
        imageUrl: "/api/placeholder/200/100"
    }
];

// Get industry list from templates
const allIndustries = Array.from(
    new Set(sampleTemplates.flatMap(template => template.industry))
).sort();

const SignatureTemplateGallery = ({ 
    open, 
    onClose, 
    onSelectTemplate, 
    profileData,
    isTrial = false 
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIndustry, setSelectedIndustry] = useState('');
    const [showPremiumOnly, setShowPremiumOnly] = useState(false);
    const [showAiGenerated, setShowAiGenerated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [aiRecommendation, setAiRecommendation] = useState(null);
    const [templates, setTemplates] = useState([]);

    // Simulate loading and AI recommendation
    useEffect(() => {
        if (open) {
            setIsLoading(true);
            setTemplates([]);
            
            // Simulate API call to load templates
            setTimeout(() => {
                setTemplates(sampleTemplates);
                setIsLoading(false);
                
                // Generate AI recommendation based on profile data
                if (profileData) {
                    const industry = getIndustryFromTitle(profileData.title);
                    const recommendedTemplate = sampleTemplates.find(
                        template => template.industry.includes(industry)
                    ) || sampleTemplates[0];
                    
                    setAiRecommendation({
                        templateId: recommendedTemplate.id,
                        reason: `Based on your role as ${profileData.title} at ${profileData.company}, 
                                this template is designed to highlight your professional presence 
                                in the ${industry} industry.`
                    });
                }
            }, 1500);
        }
    }, [open, profileData]);

    // Get industry from job title
    const getIndustryFromTitle = (title) => {
        const titleLower = title.toLowerCase();
        
        if (titleLower.includes('develop') || titleLower.includes('engineer') || 
            titleLower.includes('tech') || titleLower.includes('data')) {
            return 'Technology';
        } else if (titleLower.includes('design') || titleLower.includes('creative') || 
                  titleLower.includes('art')) {
            return 'Design';
        } else if (titleLower.includes('financ') || titleLower.includes('account') || 
                  titleLower.includes('invest')) {
            return 'Finance';
        } else if (titleLower.includes('legal') || titleLower.includes('attorney') || 
                  titleLower.includes('lawyer')) {
            return 'Legal';
        } else if (titleLower.includes('market') || titleLower.includes('brand') || 
                  titleLower.includes('content')) {
            return 'Marketing';
        } else if (titleLower.includes('health') || titleLower.includes('medic') || 
                  titleLower.includes('doctor') || titleLower.includes('nurse')) {
            return 'Healthcare';
        } else if (titleLower.includes('exec') || titleLower.includes('ceo') || 
                  titleLower.includes('director') || titleLower.includes('manager')) {
            return 'Executive';
        }
        
        return 'Technology'; // Default industry
    };

    // Filter templates based on search and filter settings
    const filteredTemplates = templates.filter(template => {
        // Search term filter
        const matchesSearch = 
            template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            template.industry.some(ind => ind.toLowerCase().includes(searchTerm.toLowerCase()));
        
        // Industry filter
        const matchesIndustry = !selectedIndustry || 
            template.industry.includes(selectedIndustry);
        
        // Premium filter
        const matchesPremium = !showPremiumOnly || template.premium;
        
        // AI generated filter
        const matchesAi = !showAiGenerated || template.aiGenerated;
        
        return matchesSearch && matchesIndustry && matchesPremium && matchesAi;
    });

    const handleSelectTemplate = (template) => {
        // Check if user is trying to select a premium template while on trial
        if (template.premium && isTrial) {
            // Do not allow selection, perhaps show an alert
            return;
        }
        
        if (onSelectTemplate) {
            onSelectTemplate(template);
            onClose();
        }
    };

    const handleIndustrySelect = (industry) => {
        setSelectedIndustry(selectedIndustry === industry ? '' : industry);
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Email Signature Templates</Typography>
                    <IconButton onClick={onClose} size="small">
                        <X size={18} />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent>
                {/* AI Recommendation */}
                {!isLoading && aiRecommendation && (
                    <Alert 
                        severity="info" 
                        icon={<Wand2 size={24} />}
                        sx={{ mb: 3, alignItems: 'flex-start' }}
                        action={
                            <Button 
                                color="inherit" 
                                size="small" 
                                onClick={() => {
                                    const template = templates.find(t => t.id === aiRecommendation.templateId);
                                    if (template) handleSelectTemplate(template);
                                }}
                                disabled={isTrial && templates.find(t => t.id === aiRecommendation.templateId)?.premium}
                            >
                                Apply
                            </Button>
                        }
                    >
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Sparkles size={16} /> AI Recommended Template
                            </Typography>
                            <Typography variant="body2">
                                {aiRecommendation.reason}
                            </Typography>
                        </Box>
                    </Alert>
                )}
                
                {/* Search and Filters */}
                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        placeholder="Search templates by name or industry..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ mb: 2 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search size={20} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {allIndustries.map(industry => (
                            <Chip
                                key={industry}
                                label={industry}
                                onClick={() => handleIndustrySelect(industry)}
                                color={selectedIndustry === industry ? "primary" : "default"}
                                variant={selectedIndustry === industry ? "filled" : "outlined"}
                            />
                        ))}
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Chip
                            icon={<Crown size={16} />}
                            label="Premium Only"
                            onClick={() => setShowPremiumOnly(!showPremiumOnly)}
                            color={showPremiumOnly ? "secondary" : "default"}
                            variant={showPremiumOnly ? "filled" : "outlined"}
                        />
                        <Chip
                            icon={<Wand2 size={16} />}
                            label="AI Generated"
                            onClick={() => setShowAiGenerated(!showAiGenerated)}
                            color={showAiGenerated ? "secondary" : "default"}
                            variant={showAiGenerated ? "filled" : "outlined"}
                        />
                    </Box>
                </Box>
                
                <Divider sx={{ mb: 3 }} />
                
                {/* Templates Grid */}
                <Grid container spacing={2}>
                    {isLoading ? (
                        // Skeleton loaders
                        Array.from(new Array(6)).map((_, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Card>
                                    <Skeleton variant="rectangular" height={120} />
                                    <CardContent>
                                        <Skeleton variant="text" />
                                        <Skeleton variant="text" width="60%" />
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))
                    ) : filteredTemplates.length > 0 ? (
                        // Template cards
                        filteredTemplates.map(template => (
                            <Grid item xs={12} sm={6} md={4} key={template.id}>
                                <Card 
                                    sx={{ 
                                        cursor: 'pointer',
                                        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: 6
                                        },
                                        opacity: template.premium && isTrial ? 0.7 : 1,
                                        position: 'relative'
                                    }}
                                    onClick={() => handleSelectTemplate(template)}
                                >
                                    {/* Premium/Popular badge */}
                                    {template.premium && (
                                        <Box 
                                            sx={{ 
                                                position: 'absolute',
                                                top: 10,
                                                right: 10,
                                                zIndex: 1,
                                                bgcolor: 'secondary.main',
                                                color: 'white',
                                                borderRadius: '12px',
                                                px: 1,
                                                py: 0.5,
                                                fontSize: '0.75rem',
                                                fontWeight: 'bold',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5
                                            }}
                                        >
                                            <Crown size={14} />
                                            Premium
                                        </Box>
                                    )}
                                    
                                    {template.popular && !template.premium && (
                                        <Box 
                                            sx={{ 
                                                position: 'absolute',
                                                top: 10,
                                                right: 10,
                                                zIndex: 1,
                                                bgcolor: 'primary.main',
                                                color: 'white',
                                                borderRadius: '12px',
                                                px: 1,
                                                py: 0.5,
                                                fontSize: '0.75rem',
                                                fontWeight: 'bold',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5
                                            }}
                                        >
                                            <Star size={14} />
                                            Popular
                                        </Box>
                                    )}
                                    
                                    {/* Lock overlay for premium templates in trial mode */}
                                    {template.premium && isTrial && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                bgcolor: 'rgba(0,0,0,0.5)',
                                                zIndex: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexDirection: 'column',
                                                color: 'white',
                                                padding: 2,
                                                textAlign: 'center'
                                            }}
                                        >
                                            <Crown size={30} />
                                            <Typography variant="subtitle2" sx={{ mt: 1 }}>
                                                Premium Template
                                            </Typography>
                                            <Typography variant="caption">
                                                Upgrade to access
                                            </Typography>
                                        </Box>
                                    )}
                                    
                                    <CardMedia
                                        component="img"
                                        height="120"
                                        image={template.imageUrl}
                                        alt={template.name}
                                    />
                                    <CardContent>
                                        <Typography gutterBottom variant="subtitle1" component="div" fontWeight="bold">
                                            {template.name}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                                            {template.industry.map(ind => (
                                                <Chip
                                                    key={ind}
                                                    label={ind}
                                                    size="small"
                                                    sx={{ fontSize: '0.7rem' }}
                                                />
                                            ))}
                                            
                                            {template.aiGenerated && (
                                                <Chip
                                                    icon={<Wand2 size={12} />}
                                                    label="AI"
                                                    size="small"
                                                    color="info"
                                                    sx={{ fontSize: '0.7rem' }}
                                                />
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))
                    ) : (
                        // No results
                        <Grid item xs={12}>
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    No templates match your search criteria
                                </Typography>
                                <Button 
                                    variant="text" 
                                    onClick={() => {
                                        setSearchTerm('');
                                        setSelectedIndustry('');
                                        setShowPremiumOnly(false);
                                        setShowAiGenerated(false);
                                    }}
                                >
                                    Clear all filters
                                </Button>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                {isTrial && (
                    <Button 
                        variant="contained" 
                        color="secondary"
                        startIcon={<Crown size={16} />}
                    >
                        Upgrade for Premium Templates
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default SignatureTemplateGallery;